import { Alert, Button, Flex, Segmented, Space, Typography, message, Modal, Form, Select, Radio } from "antd";
const { Title } = Typography;
import MasterComponent from "../../../components/Forms";
import { MasterConfigs } from "../../../interfaces/entity";
import DeveloperDetails from "./DeveloperDetails";
import { useMemo, useState } from "react";
import DrawerComponent from "../../../components/Forms/Drawer";
import ProfileCard from "../../../components/ContactInfo/ProfileCard";
import { useDispatch, useSelector } from "react-redux";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import { constants } from "../../../constants/common";
import Badge from "../../../components/Badge";
import moment from "moment";
import { formatPriceIndian } from "../../../constants/functions";
import { putData } from "../../../actions/developer";
import axios from "axios";
const { EditOutlined, DollarOutlined, DownloadOutlined, FileExcelOutlined, FilePdfOutlined } = require("@ant-design/icons");

const BenchDevelopers = () => {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [selectedDeveloper, setSelectedDeveloper] = useState(null);
    const [addNew, setAddNew] = useState(false);
    const [segment, setSegment] = useState('All');
    const [markingBillable, setMarkingBillable] = useState<string | null>(null);
    const [formSubmitTrigger, setFormSubmitTrigger] = useState(0);
    const [selectedID, setSelectedID] = useState(null);
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [exportFormat, setExportFormat] = useState<'pdf' | 'excel'>('excel');
    const [exportLoading, setExportLoading] = useState(false);
    const dispatch = useDispatch();
    const { user } = useSelector((app: any) => app?.app || {});
    const query: any = useSelector((app: any) => app?.app?.query);
    const isAdmin = user?.designation === 'Admin';

    const handleMarkAsBillable = async (developerId: string) => {
        try {
            setMarkingBillable(developerId);
            const response: any = await dispatch(putData(`/bench-developers/${developerId}/mark-billable`, {}));
            // putData returns response.data.data when code is 200, otherwise response.data
            if (response && response._id) {
                message.success('Developer marked as billable successfully');
                dispatch({
                    type: 'REFRESH_TABLE',
                    refreshTable: true
                });
            } else if (response && response.code && response.code !== 200) {
                message.error(response.message || 'Failed to mark developer as billable');
            } else {
                message.error('Failed to mark developer as billable');
            }
        } catch (error) {
            console.error('Error marking developer as billable:', error);
            message.error('Failed to mark developer as billable');
        } finally {
            setMarkingBillable(null);
        }
    };

    const handleExport = async () => {
        try {
            setExportLoading(true);

            // Build query parameters from current filters
            const queryParams: any = {
                format: exportFormat,
                vendorPartner: segment
            };

            // Add any additional filters from query state
            if (query?.fields) {
                Object.keys(query.fields).forEach(key => {
                    queryParams[key] = query.fields[key];
                });
            }
            if (query?.conditions) {
                Object.keys(query.conditions).forEach(key => {
                    queryParams[key] = query.conditions[key];
                });
            }

            // Make request to export endpoint
            const response = await axios.get(`${process.env.API_URL}/bench-developers/export`, {
                params: queryParams,
                responseType: 'blob',
            });

            // Check if response is an error (blob with JSON error message)
            if (response.data.type && response.data.type.includes('application/json')) {
                // Error response is JSON blob
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.message || 'Failed to export developers');
            }

            // Create blob and download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const extension = exportFormat === 'pdf' ? 'pdf' : 'xlsx';
            const filename = `developers_export_${moment().format('YYYY-MM-DD_HH-mm-ss')}.${extension}`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            message.success(`Developers exported successfully as ${exportFormat.toUpperCase()}`);
            setExportModalVisible(false);
        } catch (error: any) {
            console.error('Error exporting developers:', error);
            let errorMessage = 'Failed to export developers';

            if (error?.response) {
                // If response is a blob, try to parse it
                if (error.response.data instanceof Blob) {
                    try {
                        const text = await error.response.data.text();
                        const errorData = JSON.parse(text);
                        errorMessage = errorData.message || errorMessage;
                    } catch (parseError) {
                        errorMessage = error.message || errorMessage;
                    }
                } else if (error.response.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }

            message.error(errorMessage);
        } finally {
            setExportLoading(false);
        }
    };

    const config: MasterConfigs = useMemo(() => ({
        api: () => "/bench-developers",
        queryParams: {
            vendorPartner: segment
        },
        title: "Bench Developers",
        isActionTab: true,
        getColumns: () => [
            {
                title: "Developer", dataIndex: "developer", key: "developer", render: (text, record) => (
                    <div className="flex items-center gap-2 cursor-pointer" onClick={() => {
                        setAddNew(false);
                        setSelectedDeveloper(record);
                        setOpenDrawer(true);
                    }}>
                        <SmallProfileCard
                            name={constants.GET_FULL_NAME(record)}
                            subTitle={record?.jobTitle?.name || ''}
                            avatar={record.developer?.avatar}
                        />
                    </div>
                ),
                width: 200,
                fixed: 'left'
            },
            {
                title: 'Experience',
                width: 150,
                align: 'center', dataIndex: 'experience', key: 'experience', render: (text) => (text ? <span>{text} Years</span> : null)
            },
            {
                title: 'Notice Period',
                width: 150,
                align: 'center', dataIndex: 'noticePeriodDays', key: 'noticePeriodDays', render: (text) => (text ? <span>{text} Days</span> : null)
            },
            {
                title: 'LWD',
                width: 150,
                align: 'center', dataIndex: 'lastWorkingDate', key: 'lastWorkingDate', render: (text) => (text ? <span>{moment(new Date(text)).format('LL')}</span> : null)
            },
            {
                title: 'Priority',
                width: 150,
                align: 'center', dataIndex: 'priority', key: 'priority',
                render: (text) => {
                    if (!text) return null;
                    let color: any = "yellow";
                    if (text === 'High') color = "red";
                    if (text === 'Medium') color = "yellow";
                    if (text === 'Low') color = "green";
                    return <Badge color={color} text={text} />;
                },
            },
            {
                title: 'Available',
                width: 200,
                dataIndex: 'developerAvailability',
                key: 'developerAvailability',
                render: (text) => (text ? constants.DEVELOPER_AVAILABILITY.find(item => item.value === text)?.name : null)
            },
            {
                title: "Budget Range",
                width: 200,
                dataIndex: "budgetRange", key: "budgetRange", render: (text) => (text ? <span>{formatPriceIndian(text)}</span> : null)
            },
            {
                title: 'Vendor',
                width: 200,
                dataIndex: 'vendorPartner', key: 'vendorPartner', render: (text, record) => (text?.vendorName ? <Space direction="vertical">
                    {text.vendorName}
                    {record?.vendorBudget ? <span className="text-xs text-gray-500">Budget: {formatPriceIndian(record.vendorBudget)}</span> : null}
                </Space> : <span>Own Profile</span>)
            },
            {
                title: 'Profile Since',
                width: 200,
                dataIndex: 'createdAt', key: 'profileSince', render: (text) => (text ? <span>{moment(text).format('LLL')}</span> : null)
            },
            {
                title: 'Developer Owner',
                dataIndex: 'developerOwner',
                key: 'developerOwner',
                width: 200,
                render: (text) => <SmallProfileCard
                    name={constants.GET_FULL_NAME(text)}
                    avatar={text?.avatar}
                />
            },
            {
                title: 'Billable',
                dataIndex: 'billable',
                key: 'billable',
                width: 120,
                align: 'center',
                render: (billable: boolean, record: any) => {
                    if (billable) {
                        return <Badge color="green" text="Billable" />;
                    }
                    return (
                        <Button
                            type="link"
                            size="small"
                            icon={<DollarOutlined />}
                            loading={markingBillable === record._id}
                            onClick={() => {
                                Modal.confirm({
                                    title: 'Mark as Billable',
                                    content: `Are you sure you want to mark ${constants.GET_FULL_NAME(record)} as billable?`,
                                    onOk: () => handleMarkAsBillable(record._id),
                                });
                            }}
                        >
                            Mark Billable
                        </Button>
                    );
                }
            }
        ],
        actions: [
            {
                name: "Add Bench", actionType: "Add", onClick: () => {
                    setAddNew(true);
                    setSelectedDeveloper(null);
                    setOpenDrawer(true);
                }, settings: { type: "default", loading: false }
            },
            {
                name: "Export", actionType: "Custom", onClick: () => {
                    setExportModalVisible(true);
                }, settings: { type: "default", icon: <DownloadOutlined /> }
            },
        ],
        extraControles: [
            <Segmented
                key="segment"
                value={segment}
                options={['All', 'Own', 'Vendor']}
                onChange={(value) => {
                    setSegment(value);
                }}
            />
        ],
        modalTitle: (text) => text + ' Developer',
        getFormColumns: () => [
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Developer Information</Title>
            },
            {
                label: 'Developer Owner',
                name: 'developerOwner',
                type: 'select',
                isCombo: true,
                span: 12,
            },
            {
                label: 'Developer',
                name: 'developer',
                type: 'select',
                isCombo: true,
                selectProps: {
                    showSearch: true,
                    allowClear: true,
                },
                span: 12,
            },
            {
                label: 'Vendor',
                name: 'vendorPartner',
                type: 'select',
                isCombo: true,
                span: 12,
            },
            {
                label: 'First Name',
                name: 'firstName',
                type: 'text',
                inputProps: {
                    addonBefore: <Form.Item name="preName">
                        <Select style={{ width: 100 }} placeholder="Title">
                            {constants.PRENAME.map(ele => <Select.Option key={ele.value} value={ele.value}>{ele.name}</Select.Option>)}
                        </Select>
                    </Form.Item>
                },
                rules: [{ required: true, message: 'First Name is required' }],
                span: 12,
            },
            {
                label: 'Last Name',
                name: 'lastName',
                type: 'text',
                span: 12,
            },
            {
                label: 'Email',
                name: 'email',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return emailRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid email address'));
                        },
                    },
                ],
            },
            {
                label: 'Mobile',
                name: 'mobile',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const mobileRegex = /^[0-9]{10,15}$/;
                            return mobileRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid mobile number'));
                        },
                    },
                ],
            },
            {
                label: 'LinkedIn Profile',
                name: 'linkedIn',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value || (typeof value === 'string' && value.trim() === '')) {
                                return Promise.resolve();
                            }
                            const linkedInRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[\w-]+\/?$/i;
                            if (linkedInRegex.test(value.trim())) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                        },
                    },
                ],
                placeholder: 'https://www.linkedin.com/in/your-profile',
            },
            {
                label: 'Job Title',
                name: 'jobTitle',
                type: 'select',
                isCombo: true,
                span: 12,
                rules: [{ required: true, message: 'Job Title is required' }],
            },
            {
                label: 'Experience',
                name: 'experience',
                type: 'text',
                inputProps: {
                    addonAfter: 'Years',
                },
                rules: [
                    { required: true, message: 'Experience is required' },
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const experienceRegex = /^[0-9]{1,3}$/;
                            return experienceRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid experience'));
                        },
                    },
                ],
                span: 12,
            },
            {
                label: 'Developer Availability',
                name: 'developerAvailability',
                type: 'select',
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: constants.DEVELOPER_AVAILABILITY,
                rules: [{ required: true, message: 'Developer Availability is required' }],
                span: 12,
            },
            {
                label: 'Priority',
                name: 'priority',
                type: 'select',
                selectProps: {
                    options: [
                        { label: 'High', value: 'High' },
                        { label: 'Medium', value: 'Medium' },
                        { label: 'Low', value: 'Low' },
                    ],
                },
                span: 12,
            },
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Typography.Title level={5} className="font-bold border-b pb-2">Notice Information</Typography.Title>
            },
            {
                label: 'Notice Period',
                name: 'noticePeriodDays',
                type: 'text',
                inputProps: {
                    addonAfter: 'Days',
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const noticePeriodRegex = /^[0-9]{1,3}$/;
                            return noticePeriodRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid notice period'));
                        },
                    },
                ],
                span: 12,
            },
            {
                label: 'Last Working Date',
                name: 'lastWorkingDate',
                type: 'date',
                dateProps: {
                    format: 'DD/MM/YYYY',
                },
                span: 12,
            },
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Typography.Title level={5} className="font-bold border-b pb-2">Pricing Information</Typography.Title>
            },
            {
                label: 'Vendor Budget',
                name: 'vendorBudget',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const rangeRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
                            return rangeRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid vendor billing rate'));
                        },
                    },
                ],
                span: 12,
            },
            {
                label: 'Selling Price',
                name: 'budgetRange',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                rules: [
                    { required: true, message: 'Selling Price is required' },
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.reject(new Error('Selling Price is required'));
                            const budgetRange = /^\s*\d+(\.\d{1,2})?\s*-\s*\d+(\.\d{1,2})?\s*$/;
                            return budgetRange.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid budget range, e.g. 10000 - 20000'));
                        },
                    },
                ],
                span: 12,
            },
            {
                label: 'Vendor GST / LUT',
                name: 'vendorGST',
                type: 'text',
                inputProps: {
                    defaultValue: 18,
                    addonAfter: '%',
                },
                span: 12,
            },
            {
                label: 'TDS for Vendor',
                name: 'vendorTDS',
                type: 'text',
                inputProps: {
                    defaultValue: 10,
                    addonAfter: '%',
                },
                span: 12,
            },
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Typography.Title level={5} className="font-bold border-b pb-2">Description Information</Typography.Title>
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 24,
                textAreaProps: {
                    autoSize: { minRows: 3, maxRows: 6 }
                }
            },
        ],
        combo: [
            {
                api: '/users',
                name: 'developerOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            },
            {
                api: '/users',
                name: 'developer',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            },
            {
                api: '/v1/vendor',
                name: 'vendorPartner',
                idKey: '_id',
                render: (text) => text?.vendorName || 'N/A'
            },
            {
                api: '/job-titles',
                name: 'jobTitle',
                idKey: '_id',
                render: (text) => text?.name || 'N/A'
            },
        ],
        setInitialForm: () => {
            if (!isAdmin && user?._id) {
                return { developerOwner: user._id };
            }
            return {};
        },
    }), [segment, isAdmin, user]);

    const onClose = () => {
        setOpenDrawer(false);
        setSelectedDeveloper(null);
        setAddNew(false);
        setSelectedID(null);
        dispatch({
            type: 'REFRESH_TABLE',
            refreshTable: true
        });
    }

    return (
        <div>
            <MasterComponent config={config} isModal={true} recordId={selectedID} />
            <Modal
                title="Export Rate Card"
                open={exportModalVisible}
                onOk={handleExport}
                onCancel={() => setExportModalVisible(false)}
                okText="Export"
                okButtonProps={{ loading: exportLoading, icon: <DownloadOutlined /> }}
                cancelText="Cancel"
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Typography.Text strong>Select Export Format:</Typography.Text>
                    <Radio.Group
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value)}
                        style={{ width: '100%' }}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Radio value="excel">
                                <Space>
                                    <FileExcelOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
                                    <span>Excel (.xlsx)</span>
                                </Space>
                            </Radio>
                            <Radio value="pdf">
                                <Space>
                                    <FilePdfOutlined style={{ fontSize: '18px', color: '#ff4d4f' }} />
                                    <span>PDF (.pdf)</span>
                                </Space>
                            </Radio>
                        </Space>
                    </Radio.Group>
                    <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                        All developers matching current filters will be exported.
                    </Typography.Text>
                </Space>
            </Modal>
            <DrawerComponent
                title={
                    !addNew ? <Flex justify="space-between" align="center" gap={2} style={{ width: '95%' }}>
                        <ProfileCard
                            size={40}
                            name={`${selectedDeveloper?.firstName || ''} ${selectedDeveloper?.lastName || ''}`}
                            avatarUrl={selectedDeveloper?.profilePhoto ? process.env.FILE_URL + selectedDeveloper?.profilePhoto : null}
                            role={selectedDeveloper?.title || 'Lead Details'}
                        />
                        {!selectedDeveloper?.converted && <div className="flex gap-2">
                            <Button disabled type="primary">Send Email</Button>
                            <Button icon={<EditOutlined />} onClick={() => {
                                setSelectedID(selectedDeveloper?._id);
                                setOpenDrawer(false);
                            }} />
                        </div>}
                    </Flex> : 'Add New Developer'
                }
                visible={openDrawer}
                handleCancel={() => setOpenDrawer(false)}
                isView={!addNew}
                loading={false}
                submitForm={() => {
                    setFormSubmitTrigger(prev => prev + 1);
                }}
                rest={{
                    width: '90%',
                    style: {
                        background: '#eef1f9',
                        padding: '0px !important',
                    },
                    bodyStyle: {
                        padding: 0,
                        overflowY: 'auto',
                        maxHeight: 'calc(100vh - 120px)',
                        display: 'flex',
                        flexDirection: 'column'
                    },
                    headerStyle: { background: '#FFFFFF', padding: '8px 10px' },
                }}
            >
                <DeveloperDetails developer={selectedDeveloper} addNew={addNew} onClose={() => onClose()} formSubmitTrigger={formSubmitTrigger} />
            </DrawerComponent>
        </div>
    );
};

export default BenchDevelopers;